import { useState } from "react";
import { KeyRound, PlugZap, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";

const HELP = {
  mvsep_cinematic_local: { url: null, steps: "Self-hosted GPU worker (RunPod L40S now, local NVIDIA workstation later). Enter the worker URL and its bearer key; the browser never talks to the worker directly." },
  gaudio: { url: "https://www.gaudiolab.com/developers", steps: "Sign up at Gaudio Developers → your default project includes free starter credits → Project settings → API keys → Create key. The key is shown once; paste it here. Header used: x-ga-apikey." },
  audioshake: { url: "https://dashboard.audioshake.ai", steps: "Sign up at dashboard.audioshake.ai (10 free credits) → Settings → API Keys → Create new key. Header used: x-api-key." },
  gulmohar: { url: null, steps: "Point this at your future inference server (POST /separate, GET /jobs/{id}) with a bearer token." },
};

export const EngineKeyForm = ({ engine, isAdmin, onSaved }) => {
  const [key, setKey] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [test, setTest] = useState(null);
  const help = HELP[engine.key];

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/engines/${engine.key}/config`, { api_key: key, api_url: url || null });
      toast.success(`${engine.name} key saved`);
      setKey(""); setUrl("");
      onSaved();
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  const runTest = async () => {
    setBusy(true); setTest(null);
    try { const r = await api.post(`/engines/${engine.key}/test`); setTest(r.data); } catch (e) { setTest({ ok: false, message: errorText(e) }); } finally { setBusy(false); }
  };
  const clear = async () => {
    if (!window.confirm(`Remove the stored ${engine.name} API key?`)) return;
    await api.put(`/engines/${engine.key}/config`, { api_key: "" });
    onSaved();
  };

  return (
    <div data-testid={`engine-config-${engine.key}`} className="border-t border-[#253044] pt-3 space-y-2">
      {help && (
        <div className="text-[11px] text-slate-400 leading-relaxed">
          {help.steps}{" "}
          {help.url && <a href={help.url} target="_blank" rel="noreferrer" className="text-cyan-300 hover:underline inline-flex items-center gap-1">Open console <ExternalLink size={10} /></a>}
        </div>
      )}
      {isAdmin && (
        <div className="flex flex-col gap-2">
          <input data-testid={`engine-key-input-${engine.key}`} type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder={engine.api_key_masked ? `Replace key (${engine.api_key_masked})` : "Paste API key"}
            className="bg-[#0B0E14] border border-[#253044] rounded-sm px-2.5 py-1.5 text-xs font-mono focus:outline-none focus:border-cyan-500" />
          {(engine.key === "gulmohar" || engine.key === "mvsep_cinematic_local") && (
            <input data-testid={`engine-url-input-${engine.key}`} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Inference server URL"
              className="bg-[#0B0E14] border border-[#253044] rounded-sm px-2.5 py-1.5 text-xs font-mono focus:outline-none focus:border-cyan-500" />
          )}
          <div className="flex gap-1.5">
            <button data-testid={`engine-save-key-${engine.key}`} disabled={busy || !key} onClick={save} className="flex items-center gap-1.5 px-2.5 h-8 rounded-sm bg-[#E11D48] hover:bg-[#be123c] text-white text-xs font-semibold disabled:opacity-40"><KeyRound size={12} /> Save key</button>
            <button data-testid={`engine-test-${engine.key}`} disabled={busy || !engine.configured} onClick={runTest} className="flex items-center gap-1.5 px-2.5 h-8 rounded-sm border border-[#3A4A68] hover:bg-[#1E2738] text-xs disabled:opacity-40"><PlugZap size={12} /> Test connection</button>
            {engine.key_source === "settings" && <button data-testid={`engine-clear-key-${engine.key}`} onClick={clear} className="ml-auto text-[11px] text-slate-500 hover:text-rose-300">remove key</button>}
          </div>
        </div>
      )}
      {!isAdmin && <div className="text-[11px] text-slate-500">Only administrators can change API keys.</div>}
      {test && <div data-testid={`engine-test-result-${engine.key}`} className={`text-xs px-2.5 py-1.5 rounded-sm border ${test.ok ? "text-emerald-300 border-emerald-800 bg-emerald-500/10" : "text-rose-300 border-rose-800 bg-rose-500/10"}`}>{test.message}</div>}
    </div>
  );
};
